import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dot = ({ v }) => (
  <span data-testid="aeo-dot" className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold"
    style={v === true ? { background: "#E6F4EA", color: "#1E7D3C" } : v === false ? { background: "#FBE9E7", color: "#B3261E" } : { background: "var(--surface-secondary)", color: "var(--muted-warm)" }}>
    {v === true ? "✓" : v === false ? "–" : "?"}
  </span>
);

export const AeoTab = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const hdr = { headers: { "X-Admin-Key": adminKey } };

  const load = useCallback(() => {
    axios.get(`${API}/admin/aeo/report`, hdr).then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.latest?.status === "running") {
      const t = setTimeout(load, 5000);
      return () => clearTimeout(t);
    }
  }, [data, load]);

  const startScan = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/aeo/scan`, {}, hdr); } catch (e) {}
    setTimeout(() => { setBusy(false); load(); }, 800);
  };

  const latest = data?.latest;
  const running = latest?.status === "running";
  const engines = ["openai", "gemini", "claude"];
  const labels = { openai: "GPT", gemini: "Gemini", claude: "Claude" };
  const byQ = {};
  (latest?.results || []).forEach((r) => {
    byQ[r.qid] = byQ[r.qid] || { question: r.question, seg: r.seg, engines: {}, comps: new Set() };
    byQ[r.qid].engines[r.engine] = r.mentioned;
    (r.competitors || []).forEach((c) => byQ[r.qid].comps.add(c));
  });

  return (
    <div data-testid="aeo-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif-display text-2xl font-semibold">KI-Sichtbarkeit</h2>
          <p className="mt-1 text-sm" style={{ color: "var(--muted-warm)" }}>
            {data ? `${data.questions} Katalog-Fragen an ${data.engines.join(", ")} – wird Mira genannt?` : "Lade…"} Läuft automatisch jeden Montag mit dem Wochenreport.
          </p>
        </div>
        <button data-testid="aeo-scan-btn" onClick={startScan} disabled={busy || running}
          className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
          style={{ background: "var(--camel)" }}>
          {running ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
          {running ? `Scan läuft… ${latest?.progress || 0}/30` : "Scan jetzt starten"}
        </button>
      </div>

      {latest && latest.status === "done" && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="aeo-kpis">
          <div className="rounded-2xl border p-4" style={{ borderColor: "var(--line)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--muted-warm)" }}>Mira genannt</p>
            <p className="mt-1 font-serif-display text-3xl font-semibold">{latest.mentions}<span className="text-base font-normal" style={{ color: "var(--muted-warm)" }}> / {latest.answered}</span></p>
          </div>
          {engines.map((e) => (
            <div key={e} className="rounded-2xl border p-4" style={{ borderColor: "var(--line)" }}>
              <p className="text-xs uppercase tracking-wide" style={{ color: "var(--muted-warm)" }}>{labels[e]}</p>
              <p className="mt-1 font-serif-display text-3xl font-semibold">{latest.per_engine?.[e]?.mentions ?? 0}<span className="text-base font-normal" style={{ color: "var(--muted-warm)" }}> / {latest.per_engine?.[e]?.total ?? 0}</span></p>
            </div>
          ))}
        </div>
      )}

      {data?.history?.length > 1 && (
        <div className="rounded-2xl border p-4" style={{ borderColor: "var(--line)" }} data-testid="aeo-history">
          <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--muted-warm)" }}>Verlauf (Nennungen pro Scan)</p>
          <div className="mt-3 flex items-end gap-2">
            {data.history.map((h, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className="w-8 rounded-t" style={{ height: `${8 + (h.mentions || 0) * 4}px`, background: "var(--camel)" }} title={`${h.mentions}/${h.answered}`} />
                <span className="text-[10px]" style={{ color: "var(--muted-warm)" }}>{(h.date || "").slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {latest && (latest.results || []).length > 0 && (
        <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }}>
          <table className="w-full text-left text-sm" data-testid="aeo-table">
            <thead>
              <tr style={{ background: "var(--surface-secondary)" }}>
                <th className="px-4 py-3 font-semibold">Frage</th>
                <th className="px-3 py-3 font-semibold">GPT</th>
                <th className="px-3 py-3 font-semibold">Gemini</th>
                <th className="px-3 py-3 font-semibold">Claude</th>
                <th className="px-4 py-3 font-semibold">Wer wird stattdessen genannt</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byQ).map(([qid, row]) => (
                <tr key={qid} className="border-t align-top" style={{ borderColor: "var(--line)" }} data-testid={`aeo-row-${qid}`}>
                  <td className="px-4 py-3">
                    <span className="mr-2 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "var(--surface-secondary)", color: "var(--muted-warm)" }}>{row.seg}</span>
                    {row.question}
                  </td>
                  {engines.map((e) => (<td key={e} className="px-3 py-3"><Dot v={row.engines[e]} /></td>))}
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{[...row.comps].join(", ") || "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {latest?.top_competitors?.length > 0 && (
        <div className="rounded-2xl border p-4" style={{ borderColor: "var(--line)" }} data-testid="aeo-competitors">
          <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--muted-warm)" }}>Meistgenannte Wettbewerber (letzter Scan)</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {latest.top_competitors.map((c) => (
              <span key={c.name} className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: "var(--line)" }}>{c.name} · {c.count}×</span>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs" style={{ color: "var(--muted-warm)" }}>
        Ehrlicher Hinweis: Gemessen werden die Modell-Antworten über die API (Trainingswissen, ohne Live-Websuche). Die Antworten der Consumer-Oberflächen (ChatGPT mit Suche, Perplexity) können abweichen – der Trend über die Wochen ist die verlässliche Grösse.
      </p>
    </div>
  );
};
